import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { getCleanSubjectDetails, Icons } from '../../../utils/utilities'
import * as Yup from 'yup'
import React, { useCallback, useEffect, useState } from 'react'
import { useUserStore } from '../../../store/user.store'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react'
import CustomSelectField from '../../common/CustomSelectField'
import CustomChip from '../../common/CustomChip'

const subjectFilterFunc = ( sub, values ) => {

    if ( sub.ac_year_id !== values.academicYear ) return false

    return true
}

const AddQuestionBank = observer( ( { state, setState, subject = null } ) => {

    const UserStore = useUserStore()
    const QuizStore = useQuizAndQuestionbankStore()

    const [refreshing, setRefreshing] = useState( false )
    const [loading, setLoading] = useState( false )
    const [adding, setAdding] = useState( false )

    const navigate = useNavigate()

    const { palette, layout, border } = useTheme()

    const initialValues = {
        name: '',
        subject: subject || '-',
        academicYear: UserStore.getUserAcademicYears[0]?.ac_year_id || ""
    }

    const validationSchema = Yup.object().shape( {
        name: Yup.string().required( "Question bank name is required!" ).max( 240, "Max length 240 characters" ),
        subject: Yup.mixed().required( 'Subject is required' ).notOneOf( ["-"], "Please select a question bank subject" )
    } )

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const addQuestionBank = async ( values, { resetForm } ) => {
        setAdding( true )
        values.subject = getCleanSubjectDetails( values.subject )
        const success = await QuizStore.addQuestionBank( values, subject?.subject_id )

        if ( success ) {
            resetForm()
            setState( false )
            navigate( `${success.data.id}` )
        }
        setAdding( false )
    }


    const getFacultySubjects = useCallback( async () => {
        setLoading( true )
        await UserStore.fetchUserSubjects( Boolean( UserStore.getUserSubjectsDetails.cached ) )
        setLoading( false )
    }, [UserStore] )

    useEffect( () => {
        if ( UserStore.getErrorsStatuses?.subjects === true )
            getFacultySubjects()
    }, [UserStore, getFacultySubjects] )

    return (
        <Dialog open={state} slotProps={{
            paper: { sx: { width: "90%", maxWidth: "650px", background: palette.form.formCardBg } }
        }}>
            <DialogTitle component="h2" sx={{ borderBottom: border[1], padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: "600", fontSize: "16px", }}>
                Create Question Bank
            </DialogTitle>
            <DialogContent>
                <Box>
                    <Formik onSubmit={addQuestionBank} initialValues={initialValues} validationSchema={validationSchema}>
                        {( { values, setFieldValue, resetForm } ) => (
                            <Form>
                                <Box borderRadius="5px"  >
                                    <FormControl fullWidth margin='normal'>
                                        <Typography component="label" htmlFor="QB-name" display="block" variant="subtitle2" gutterBottom color={palette.form.labelColor}>Question bank name *</Typography>
                                        <Field autoFocus id="QB-name" size="small" sx={{ fontSize: "14px" }} InputProps={{ sx: { fontSize: "14px" } }} placeholder="Eg: Data Structures question bank" as={TextField} name="name" type="text" />
                                        <Typography fontSize="12px" color="error.main"> <ErrorMessage name='name' /> </Typography>
                                    </FormControl>

                                    <FormControl margin='normal' fullWidth>
                                        <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                            <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                                Select academic year *
                                            </Typography>
                                            <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                                {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                            </Button>
                                        </Box>
                                        <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => setFieldValue( 'academicYear', event.target.value )} value={values.academicYear || ""} />
                                    </FormControl>

                                    <FormControl margin="normal" fullWidth>
                                        <Typography component="label" htmlFor="QB-subject" display="block" variant="subtitle2" gutterBottom color={palette.form.labelColor}>Select a question bank subject  * {subject !== null && "(Pre-filled)"} </Typography>

                                        <CustomSelectField
                                            id="QB-subject"
                                            size="small"
                                            renderValue={selected => {
                                                if ( selected ) {
                                                    const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => sub.unique_subject_key === selected )
                                                    if ( subject )
                                                        return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                                    else
                                                        return "Subject"
                                                } else {
                                                    return "Subject"
                                                }
                                            }}
                                            itemValueKey="unique_subject_key"
                                            itemIdentityKey="unique_subject_key"
                                            data={( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => subjectFilterFunc( sub, values ) )}
                                            disabled={!values.academicYear}
                                            sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                            value={values?.subject?.unique_subject_key || ""}
                                            onChange={( e ) => {
                                                setFieldValue( 'subject', UserStore.getUserSubjectsDetails?.currentSubjects?.find( s => s.unique_subject_key === e.target.value ) )
                                            }}
                                            name="subject"
                                            renderOptionText={item => <Box display="flex" flexDirection="column">
                                                <Typography textTransform="capitalize" variant='subtitle2'>
                                                    {item.subject_name?.toLowerCase()}
                                                </Typography>
                                                <Box display="flex" gap="5px" alignItems="center">
                                                    <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                                    <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                                    {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                                </Box>
                                            </Box>}
                                        />


                                        {!subject && UserStore.getErrorsStatuses?.subjects && <Box bgcolor={palette.error.light + "22"} marginTop="10px" borderRadius="5px" padding="5px 10px" border={`1px dotted ${palette.error.main}`} display="flex" alignItems="center" gap="5px" fontSize="12px" justifyContent="space-between" color="textSecondary">
                                            <Typography color="error.main" variant='subtitle2'>Failed to fetch <strong><em>subjects</em></strong> from ERP...</Typography>
                                            <Button color="error" onClick={() => getFacultySubjects()} endIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay}>Retry</Button>
                                        </Box>}
                                        {!subject && loading && !UserStore.getErrorsStatuses?.subjects && <Typography marginTop="5px" display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary"> <DataLoadingSpinner padding='0' size={14} /> {`Fetching your subjects from ERP...`} </Typography>}
                                        <Typography marginTop="5px" fontSize="12px" color="error.main"> <ErrorMessage name='subject' /> </Typography>
                                    </FormControl>

                                    <Box display="flex" marginTop="30px" gap={layout.cardsGap} flexWrap="wrap" alignItems="center">
                                        <Button variant="contained" disabled={adding} disableElevation type="submit" startIcon={adding ? <CircularProgress size={14} /> : Icons.default.AddIcon}>{adding ? "Adding new question bank..." : "Add Question bank"}</Button>
                                        <Button disabled={adding} startIcon={Icons.default.CloseIcon} onClick={() => { setState( false ); resetForm() }} variant="outlined" disableElevation color="error" >Cancel</Button>
                                    </Box>
                                </Box>

                            </Form>
                        )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog >
    )
} )

export default AddQuestionBank
